// History for one workflow: did it work, who did it work on, and what exactly
// did it do to them.
//
// Three levels, in that order, because that is the order people ask. The
// sentence at the top answers it for anyone who reads nothing else. The lead
// rows are the receipts. Opening a lead shows every touch with the reason given
// at the time — automation that writes to your leads has to be auditable down
// to the individual message, or nobody will leave it switched on.

import React, { useMemo, useState } from "react";
import { X, ChevronRight, Users, Send, Reply, Clock } from "lucide-react";
import Drawer from "../../../overlays/Drawer";
import Badge from "../../../atoms/Badge";
import { uiOf } from "./chips";
import { OUTCOMES, summariseHistory, historyHeadline, actionOf } from "./workflowRules.mjs";

const TOUCH_LABEL = { wait: "Waiting", stop: "Ended" };
const labelFor = (action) => TOUCH_LABEL[action] || actionOf(action).label;

const BY_LABEL = {
  auto: null, // the default; saying "sent automatically" on every row is noise
  approved: "you approved it",
  waiting: "waiting on you",
  lead: "they did this",
};

const FILTERS = [
  { id: "all", label: "Everything" },
  { id: "open", label: "Still working" },
  { id: "win", label: "Came back" },
  { id: "cold", label: "Went nowhere" },
];

function matchesFilter(run, filter) {
  const o = OUTCOMES[run.outcome];
  if (filter === "open") return !o?.done;
  if (filter === "win") return !!o?.win;
  if (filter === "cold") return !!o?.done && !o?.win;
  return true;
}

// Solid rule down the left edge instead of a tinted fill, so three tiles in a
// row stay readable and match the chips on the workflow list.
function StatTile({ icon: Icon, value, label, ink }) {
  return (
    <div style={{
      flex: 1, minWidth: 0, padding: "8px 11px", borderRadius: 8,
      background: "var(--color-bg-surface)",
      border: "1px solid var(--color-border-subtle)",
      borderLeft: `3px solid ${ink}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--color-text-muted)", fontSize: 11 }}>
        <Icon size={11} /> {label}
      </div>
      <div style={{
        fontSize: 20, fontWeight: 600, marginTop: 1,
        color: "var(--color-text-primary)", fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </div>
    </div>
  );
}

// One touch. The note is the reason recorded at the time, kept verbatim —
// paraphrasing it into a status word turns an audit trail back into a black box.
function Touch({ t, last }) {
  const { icon: Icon, color } = uiOf(t.action);
  const by = BY_LABEL[t.by];

  return (
    <li style={{ display: "flex", gap: 10, minWidth: 0 }}>
      {/* icon column doubles as the timeline rail */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <span style={{
          width: 22, height: 22, borderRadius: 6, flexShrink: 0,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: color, color: "#fff",
        }}>
          <Icon size={11} strokeWidth={2.2} />
        </span>
        {!last && <span style={{ flex: 1, width: 1, minHeight: 12, background: "var(--color-border-subtle)", marginTop: 3 }} />}
      </div>
      <div style={{ minWidth: 0, flex: 1, paddingBottom: last ? 0 : 12 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--color-text-primary)" }}>{t.label}</span>
          <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t.at}</span>
          <span style={{ fontSize: 10.5, color: "var(--color-text-muted)" }}>{labelFor(t.action)}</span>
          {by && (
            <span style={{
              fontSize: 10, fontWeight: 500, padding: "1px 6px", borderRadius: 99,
              background: "var(--color-bg-muted)", color: "var(--color-text-secondary)",
            }}>
              {by}
            </span>
          )}
        </div>
        {t.note && (
          <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)", lineHeight: "17px", marginTop: 3 }}>
            {t.note}
          </div>
        )}
      </div>
    </li>
  );
}

function LeadRun({ run, open, onToggle }) {
  const outcome = OUTCOMES[run.outcome] || OUTCOMES.working;
  const sent = run.touches.filter((t) => t.action !== "wait" && t.action !== "stop").length;

  return (
    <div style={{ borderBottom: "1px solid var(--color-border-subtle)" }}>
      <button
        type="button"
        onClick={onToggle}
        className="tg-filter-opt"
        aria-expanded={open}
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%",
          padding: "11px 16px", border: "none", cursor: "pointer",
          textAlign: "left", borderRadius: 0,
        }}
      >
        <ChevronRight
          size={13}
          style={{
            flexShrink: 0, color: "var(--color-text-muted)",
            transform: open ? "rotate(90deg)" : "none", transition: "transform 140ms ease",
          }}
        />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{
            display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {run.lead}
          </span>
          <span style={{ display: "block", fontSize: 11, color: "var(--color-text-muted)", marginTop: 1 }}>
            {sent === 0 ? "nothing sent" : `${sent} ${sent === 1 ? "message" : "messages"}`} · since {run.started}
          </span>
        </span>
        <Badge tone={outcome.tone}>{outcome.label}</Badge>
      </button>

      {open && (
        <ul style={{ listStyle: "none", margin: 0, padding: "2px 16px 14px 39px" }}>
          {run.touches.map((t, i) => (
            <Touch key={`${run.id}-${i}`} t={t} last={i === run.touches.length - 1} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default function HistoryPanel({ open, onClose, workflow, runs = [] }) {
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const sum = useMemo(() => summariseHistory(runs), [runs]);
  const shown = useMemo(() => runs.filter((r) => matchesFilter(r, filter)), [runs, filter]);

  return (
    <Drawer open={open} onClose={onClose} width={560}>
      <div style={{
        flexShrink: 0, display: "flex", alignItems: "flex-start", gap: 12,
        padding: "16px 16px 14px", borderBottom: "1px solid var(--color-border-subtle)",
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 500 }}>
            History
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--color-text-primary)", marginTop: 3 }}>
            {workflow?.name || "Workflow"}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", marginTop: 3 }}>
            {historyHeadline(sum)}
          </div>
        </div>
        <button
          type="button" onClick={onClose} aria-label="Close"
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-muted)", padding: 2, flexShrink: 0 }}
        >
          <X size={16} />
        </button>
      </div>

      <div style={{ flexShrink: 0, display: "flex", gap: 8, padding: "12px 16px" }}>
        <StatTile icon={Users} value={sum.leads} label="Leads" ink={uiOf("task").color} />
        <StatTile icon={Send} value={sum.touches} label="Sent" ink={uiOf("email").color} />
        <StatTile icon={Reply} value={sum.wins} label="Came back" ink={uiOf("whatsapp").color} />
      </div>

      {sum.waiting > 0 && (
        <div style={{
          flexShrink: 0, margin: "0 16px 12px", padding: "9px 11px", borderRadius: 8,
          background: "var(--crm-warning)", color: "#fff",
          fontSize: 12, display: "flex", alignItems: "center", gap: 7,
        }}>
          <Clock size={12} />
          {sum.waiting} {sum.waiting === 1 ? "draft is" : "drafts are"} waiting for you to approve.
        </div>
      )}

      <div style={{ flexShrink: 0, display: "flex", gap: 5, padding: "0 16px 12px", flexWrap: "wrap" }}>
        {FILTERS.map((f) => {
          const count = runs.filter((r) => matchesFilter(r, f.id)).length;
          const active = filter === f.id;
          return (
            <button
              key={f.id} type="button" onClick={() => setFilter(f.id)}
              aria-pressed={active}
              style={{
                fontSize: 12, padding: "4px 10px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${active ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
                background: active ? "var(--crm-primary)" : "transparent",
                color: active ? "var(--crm-primary-text)" : "var(--color-text-secondary)",
                fontWeight: active ? 500 : 400,
              }}
            >
              {f.label} {count > 0 && <span style={{ opacity: 0.7 }}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", borderTop: "1px solid var(--color-border-subtle)" }}>
        {shown.map((r) => (
          <LeadRun
            key={r.id} run={r} open={openId === r.id}
            onToggle={() => setOpenId((cur) => (cur === r.id ? null : r.id))}
          />
        ))}
        {shown.length === 0 && (
          <div style={{ padding: "28px 20px", textAlign: "center", fontSize: 12.5, color: "var(--color-text-muted)" }}>
            {runs.length === 0
              ? "No leads have been through this workflow yet."
              : "No leads in this group."}
          </div>
        )}
      </div>
    </Drawer>
  );
}
